import React, { useEffect, ReactNode } from 'react'
import { Formik, Form } from 'formik'
import { ObjectSchema } from 'yup'
import Field from 'components/Shared/Fields'
import { ModalContainer } from './style'
import RectangleButton from 'particles/Button/RectangleButton'
import { useTranslation } from 'react-i18next'
import { ModalProps as BaseModalProps } from 'particles/Modal'
import UpdateAdditionalFee from 'components/Campaign/common/Modals/UpdateAdditionalFee'
import DeleteAdditionalFee from 'components/Campaign/common/Modals/DeleteAddtionalFee'
import UpdateServiceFee from 'components/Campaign/common/Modals/UpdateServiceFee'
import UpdateTax from 'components/Campaign/common/Modals/UpdateTax'

export enum MODAL_TYPE {
    ADD_PAYMENT_ADJUSTMENT = 'addPaymentAdjustment',
    EDIT_PAYMENT_ADJUSTMENT = 'editPaymentAdjustment',
    DELETE_PAYMENT_ADJUSTMENT = 'deletePaymentAdjustment',
    EDIT_SERVICE_FEE = 'editServiceFee',
    UPDATE_TAX = 'updateTax',
}
export interface ModalState {
    type: MODAL_TYPE | undefined
}

export interface UpdateTaxState {
    tax: number
}

export interface AddServiceFeeState {
    serviceFee: number
}

export interface AddAdditionalFeeState {
    title: string
    amount: number | null
}

export type ModalSize = 's' | 'm' | 'l'
interface ModalProps extends BaseModalProps {
    onClose: () => void
    initialValues: AddServiceFeeState | AddAdditionalFeeState | UpdateTaxState
    validationSchema: ObjectSchema<any>
    onSubmit: (value: any, action?: any) => void
    submitButtonText: string
    cancelButtonText?: string
    children: ReactNode
    isLoading?: boolean
    loadingText?: string
    isDisabled?: boolean
}

const Modal = ({
    onClose,
    initialValues,
    validationSchema,
    onSubmit,
    submitButtonText,
    cancelButtonText,
    subtext,
    title,
    children,
    isLoading = false,
    loadingText,
    size = 'm',
    isDisabled = false,
    isClosable = true,
    ...props
}: ModalProps): JSX.Element => {
    const [t] = useTranslation()

    const handleSearchMode = (e: Event): void => {
        const target = e.target as HTMLElement
        if (target?.id === 'modal-container') {
            onClose()
        }
    }

    useEffect(() => {
        if (!isClosable) return
        window.addEventListener('click', handleSearchMode)
        return (): void => window.removeEventListener('click', handleSearchMode)
    }, [])

    return (
        <ModalContainer
            size={size}
            hasButtonSet={false}
            hasIcon={false}
            title={title}
            subtext={subtext}
            onClose={() => {
                if (isLoading) return
                onClose()
            }}
            isClosable={isClosable}
            {...props}
        >
            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {(formikBag): JSX.Element => {
                    return (
                        <Form className="popup-content">
                            <div className="content">
                                {children}
                                <div className="button-group">
                                    {isClosable && (
                                        <RectangleButton
                                            size="l"
                                            form="outlined"
                                            label={cancelButtonText ?? t('button.cancel')}
                                            onClick={e => {
                                                e.preventDefault()
                                                onClose()
                                            }}
                                            isLoading={isLoading}
                                            loadingText={loadingText}
                                            isDisabled={isDisabled}
                                        />
                                    )}
                                    <RectangleButton
                                        size="l"
                                        type="submit"
                                        form="filled"
                                        label={submitButtonText}
                                        onClick={e => {
                                            e.preventDefault()
                                            formikBag.submitForm()
                                        }}
                                        isLoading={isLoading}
                                        loadingText={loadingText}
                                        isDisabled={isDisabled}
                                        className="submit-button"
                                    />
                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </ModalContainer>
    )
}

Modal.TextInput = Field.TextInputField
Modal.NumberInput = Field.NumberInputField

export default Modal

const PredefinedModal: React.FC<any> = ({ type, ...props }) => {
    switch (type) {
        case MODAL_TYPE.ADD_PAYMENT_ADJUSTMENT:
        case MODAL_TYPE.EDIT_PAYMENT_ADJUSTMENT:
            return <UpdateAdditionalFee {...props} type={type} />
        case MODAL_TYPE.DELETE_PAYMENT_ADJUSTMENT:
            return <DeleteAdditionalFee {...props} type={type} />
        case MODAL_TYPE.EDIT_SERVICE_FEE:
            return <UpdateServiceFee {...props} type={type} />
        case MODAL_TYPE.UPDATE_TAX:
            return <UpdateTax {...props} type={type} />
        default:
            return <></>
    }
}

export { PredefinedModal }
